import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import SeverityBadge from "@/components/shared/SeverityBadge";
import { downloadReport } from "@/lib/downloadReport";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { extractApiError } from "@/lib/api";
import { Eye, Loader2, RefreshCw } from "lucide-react";
import LocationLabel from "@/components/shared/LocationLabel";

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function toIso(value) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function toDisplayText(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => toDisplayText(item)).join(", ");
  if (typeof value === "object") {
    const lat = value.lat ?? value.latitude;
    const lon = value.lon ?? value.lng ?? value.longitude;
    if (lat !== undefined && lon !== undefined) {
      return `${lat}, ${lon}`;
    }
    if (typeof value.address === "string" && value.address.trim()) return value.address;
    if (typeof value.place === "string" && value.place.trim()) return value.place;
    if (typeof value.name === "string" && value.name.trim()) return value.name;
    return JSON.stringify(value);
  }
  return String(value);
}

function getPrimaryVehicleLabel(item) {
  if (Array.isArray(item?.vehicles) && item.vehicles.length > 0) {
    const primaryVehicle = item.vehicles.find((vehicle) => vehicle?.role === "PRIMARY") || item.vehicles[0];
    return toDisplayText(primaryVehicle?.registration_number || primaryVehicle?.vehicle_id || "—");
  }

  return toDisplayText(item?.vehicle || item?.vehicle_plate || item?.plate || "—");
}

function getIncidentType(item) {
  return toDisplayText(item?.incident_type || item?.type || item?.category || item?.title || "Incident");
}

function getLocationLabel(item) {
  return toDisplayText(item?.location || item?.address || item?.place || "—");
}

function normalizeIncident(item) {
  return {
    id: item?.id || item?.incident_id || item?.incidentId || "",
    type: getIncidentType(item),
    vehicle: getPrimaryVehicleLabel(item),
    location: getLocationLabel(item),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    severity: String(item?.severity || "low").toLowerCase(),
    closedAt: item?.resolved_at || item?.closed_at || item?.updated_at || item?.timestamp || item?.created_at || "",
    raw: item,
  };
}

function normalizeIncidentDetail(payload) {
  return payload?.data || payload || null;
}

function readField(data, keys = []) {
  for (const key of keys) {
    if (data?.[key] !== undefined && data?.[key] !== null && data?.[key] !== "") return data[key];
  }
  return null;
}

export default function PoliceCompletedCases() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({ fromDt: "", toDt: "" });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [incidentDetail, setIncidentDetail] = useState(null);

  const fetchCompletedCases = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        status: "RESOLVED",
        page,
        page_size: pageSize,
      };

      const fromIso = toIso(appliedFilters.fromDt);
      const toIsoValue = toIso(appliedFilters.toDt);
      if (fromIso) params.from_dt = fromIso;
      if (toIsoValue) params.to_dt = toIsoValue;

      const { data } = await api.get("/incidents", { params });
      const list = normalizeCollection(data)
        .map(normalizeIncident)
        .filter((item) => item.id)
        .map((item, index) => ({
          ...item,
          displayId: `CASE-${String((page - 1) * pageSize + index + 1).padStart(4, "0")}`,
        }));

      setCases(list);
      setMeta(
        data?.meta || {
          total: list.length,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message);
      setCases([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters.fromDt, appliedFilters.toDt, page, pageSize]);

  useEffect(() => {
    fetchCompletedCases();
  }, [fetchCompletedCases]);

  const handleApplyFilters = () => {
    if (fromDt && toDt && new Date(fromDt) > new Date(toDt)) {
      toast.error("From date cannot be later than To date");
      return;
    }
    setPage(1);
    setAppliedFilters({ fromDt, toDt });
  };

  const handleResetFilters = () => {
    setFromDt("");
    setToDt("");
    setPage(1);
    setAppliedFilters({ fromDt: "", toDt: "" });
  };

  const openCaseDetail = async (item) => {
    if (!item?.id) return;

    setSelectedCase(item);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setIncidentDetail(null);

    try {
      const { data } = await api.get(`/incidents/${item.id}`);
      setIncidentDetail(normalizeIncidentDetail(data));
    } catch (err) {
      const { message } = extractApiError(err);
      setDetailError(message || "Failed to load incident details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const summary = useMemo(() => {
    const detail = incidentDetail || selectedCase?.raw || {};
    return {
      status: readField(detail, ["status", "current_status"]) || "RESOLVED",
      location: getLocationLabel(detail) || selectedCase?.location || "—",
      locationRaw: detail?.location ?? detail?.address ?? detail?.place ?? selectedCase?.locationRaw ?? null,
      vehicle: getPrimaryVehicleLabel(detail) || selectedCase?.vehicle || "—",
      severity: String(readField(detail, ["severity"]) || selectedCase?.severity || "low").toLowerCase(),
      closedAt:
        readField(detail, ["resolved_at", "closed_at", "updated_at", "timestamp", "created_at"])
        || selectedCase?.closedAt
        || "",
      type: getIncidentType(detail) || selectedCase?.type || "Incident",
    };
  }, [incidentDetail, selectedCase]);

  return (
    <div className="space-y-6" data-testid="police-completed">
      <PageHeader
        title="Completed Cases"
        subtitle="Resolved / closed incidents from the incidents API."
        onDownload={() => {
          downloadReport({
            filename: "police_completed_cases",
            title: "Police - Completed Cases Report",
            headers: ["Case", "Type", "Vehicle", "Location", "Severity", "Closed At"],
            rows: cases.map((i) => [i.id, i.type, i.vehicle, i.location, i.severity, formatDateTime(i.closedAt)]),
          });
          toast.success("Report downloaded");
        }}
        actions={(
          <Button variant="outline" size="sm" className="gap-2" onClick={fetchCompletedCases} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        )}
      />

      <SectionCard>
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_140px_auto_auto] md:items-end">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-500">From date-time</Label>
            <Input type="datetime-local" value={fromDt} onChange={(e) => setFromDt(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-500">To date-time</Label>
            <Input type="datetime-local" value={toDt} onChange={(e) => setToDt(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-500">Page size</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={String(pageSize)}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={String(size)}>{size}</option>
              ))}
            </select>
          </div>
          <Button onClick={handleApplyFilters}>Apply</Button>
          <Button variant="ghost" onClick={handleResetFilters}>Reset</Button>
        </div>
      </SectionCard>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-slate-500">
          {loading ? "Loading completed cases..." : `${meta.total} cases found`}
        </span>
        <span className="text-xs text-slate-500">Page {meta.page || page} of {meta.pages || 1}</span>
      </div>

      <SectionCard padded={false}>
        <DataTable
          data={cases}
          loading={loading}
          loadingMessage="Loading completed cases..."
          emptyMessage="No resolved incidents found for the selected filters."
          onRowClick={openCaseDetail}
          columns={[
            { key: "type", label: "Type" },
            { key: "vehicle", label: "Vehicle", cellClassName: "font-mono text-xs" },
            {
              key: "location", label: "Location", cellClassName: "text-slate-700",
              render: (i) => <LocationLabel locationValue={i.locationRaw ?? i.location} />,
            },
            { key: "severity", label: "Severity", render: (i) => <SeverityBadge severity={i.severity} /> },
            {
              key: "closed-at", label: "Closed at",
              cellClassName: "text-xs text-slate-500",
              render: (i) => formatDateTime(i.closedAt),
            },
            {
              key: "actions", label: "Actions", className: "text-right", cellClassName: "text-right",
              render: (i) => (
                <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => openCaseDetail(i)}>
                  <Eye size={14} /> View
                </Button>
              ),
            },
          ]}
        />
      </SectionCard>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" disabled={loading || page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
          Previous
        </Button>
        <Button
          variant="outline"
          disabled={loading || page >= (meta.pages || 1)}
          onClick={() => setPage((prev) => Math.min(meta.pages || 1, prev + 1))}
        >
          Next
        </Button>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl rounded-[24px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Case detail</DialogTitle>
            <DialogDescription className="font-bold text-slate-500">Reference: {selectedCase?.displayId || "—"}</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-14 text-slate-500">
              <Loader2 size={20} className="mr-2 animate-spin" /> Loading incident detail...
            </div>
          ) : detailError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{detailError}</div>
          ) : (
            <div className="space-y-4 py-1">
              <div className="flex flex-wrap items-center gap-2">
                <SeverityBadge severity={summary.severity} />
                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                  {summary.status}
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Type</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{summary.type}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Closed at</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(summary.closedAt)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Vehicle</p>
                  <p className="mt-1 font-mono text-sm font-semibold text-slate-900">{summary.vehicle}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Location</p>
                  <LocationLabel locationValue={summary.locationRaw ?? summary.location} className="mt-1 text-sm font-semibold text-slate-900" />
                </div>
              </div>

            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
